// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import { ProcessedModelRecord, Asset, AssetGroup, fetchProcessedModelsForGroup } from "./utils";

interface ProcessedModelTableProps {
  assets: Asset[];
  assetGroup: AssetGroup;
  onAddNew: () => void;
  onEdit: (model: ProcessedModelRecord) => void;
}

const ProcessedModelTable: React.FC<ProcessedModelTableProps> = ({
  assets,
  assetGroup,
  onAddNew,
  onEdit,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const [models, setModels] = useState<ProcessedModelRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user?.details?.groups?.includes("Admin");
  const isAdminReadOnly = user?.details?.groups?.includes("AdminReadOnly");

  useEffect(() => {
    loadProcessedModels();
  }, [assets]);

  const loadProcessedModels = async () => {
    try {
      setLoading(true);
      setError(null);
      const assetIds = assets.map((asset) => asset.id);
      const fetchedModels = await fetchProcessedModelsForGroup(assetIds);
      setModels(fetchedModels);
    } catch (err) {
      console.error("Error loading ProcessedModels:", err);
      setError("Failed to load ProcessedModels");
    } finally {
      setLoading(false);
    }
  };

  const handleManageFiles = (model: ProcessedModelRecord) => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.processedModelFiles,
        properties: {
          __modalSize: "lg",
          model: {
            id: model.id,
            label: model.label,
            routeLong: model.routeLong,
            routeShort: model.routeShort,
            organizationId: model.organizationId,
          },
        },
      })
    );
  };

  const getAssetName = (assetId: string): string => {
    const asset = assets.find((a) => a.id === assetId);
    return asset?.name || "Unknown Asset";
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading ProcessedModels...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4 px-6">
        <h2 className="text-xl font-semibold text-gray-900">
          ProcessedModels for {assetGroup.name}
        </h2>
        <button
          onClick={onAddNew}
          className="eai-btn-grn px-4 py-2"
        >
          Add New Model +
        </button>
      </div>

      {models.length === 0 ? (
        <div className="flex items-center justify-center p-8">
          <div className="text-gray-500">
            No ProcessedModels found. Click "Add New Model" to create one.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-y border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Label
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asset Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Route Short
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
                {(isAdmin || isAdminReadOnly) && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Files
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {models.map((model) => (
                <tr
                  key={model.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {model.label}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 font-mono">
                    {model.id}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {getAssetName(model.assetId)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        model.modelType === "PROCESSED"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-purple-100 text-purple-700"
                      }`}
                    >
                      {model.modelType}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 font-mono">
                    {model.routeShort}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {model.owner}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {model.organizationId || "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(model.createdAt)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <button
                      onClick={() => onEdit(model)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Edit
                    </button>
                  </td>
                  {(isAdmin || isAdminReadOnly) && (
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => handleManageFiles(model)}
                        className="text-eai-primary-accent hover:underline font-medium"
                      >
                        Manage Files
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProcessedModelTable;
